import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { 
  Video, Music2, Volume2, Sparkles, Download, 
  ChevronDown, ChevronUp, Loader2, Lock, Crown,
  Wand2, FileAudio, Archive, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VideoWorkflowPanel = ({ 
  musicId,
  onProcessComplete,
  disabled = false
}) => {
  const { token, user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [loudnessPresets, setLoudnessPresets] = useState([]);
  
  // Settings
  const [selectedPreset, setSelectedPreset] = useState('social_media');
  const [fadeIn, setFadeIn] = useState(0.5);
  const [fadeOut, setFadeOut] = useState(1.5);
  const [targetDuration, setTargetDuration] = useState(30);
  
  const tier = user?.tier || 'starter';
  const isFounder = user?.is_founder;
  const canExportWav = isFounder || ['pro', 'elite', 'agency', 'studio'].includes(tier);
  const canExportStems = isFounder || ['elite', 'agency', 'studio'].includes(tier);

  // Fetch loudness presets
  useEffect(() => {
    const fetchPresets = async () => {
      if (!token) return;
      try {
        const response = await axios.get(`${API}/music/loudness-presets`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setLoudnessPresets(Object.values(response.data.presets || {}));
      } catch (error) {
        console.error('Failed to fetch presets:', error);
      }
    };
    fetchPresets();
  }, [token]);

  const handleMasterForVideo = async () => {
    if (!musicId || !token) return;
    
    setProcessing(true);
    setProcessingStatus('Mastering audio...');
    
    try {
      const response = await axios.post(
        `${API}/music/${musicId}/video-workflow/master`,
        { preset: selectedPreset, apply_compression: true, apply_limiter: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Audio mastered for video!');
        onProcessComplete?.(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Mastering failed');
    } finally {
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  const handleNormalize = async () => {
    if (!musicId || !token) return;
    
    setProcessing(true);
    setProcessingStatus('Normalizing loudness...');
    
    try {
      const response = await axios.post(
        `${API}/music/${musicId}/video-workflow/normalize`,
        { preset: selectedPreset },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Loudness normalized!');
        onProcessComplete?.(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Normalization failed');
    } finally {
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  const handleAutoFade = async () => {
    if (!musicId || !token) return;
    
    setProcessing(true);
    setProcessingStatus('Applying fades...');
    
    try {
      const response = await axios.post(
        `${API}/music/${musicId}/video-workflow/auto-fade`,
        { fade_in: fadeIn, fade_out: fadeOut },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Fades applied!');
        onProcessComplete?.(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply fades');
    } finally {
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  const handleSyncDuration = async () => {
    if (!musicId || !token) return;
    
    setProcessing(true);
    setProcessingStatus('Syncing to duration...');
    
    try {
      const response = await axios.post(
        `${API}/music/${musicId}/video-workflow/sync`,
        { 
          target_duration: targetDuration,
          fade_in: fadeIn,
          fade_out: fadeOut,
          strategy: 'auto'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(`Synced to ${targetDuration}s!`);
        onProcessComplete?.(response.data);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Sync failed');
    } finally {
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  const handleExportWav = async () => {
    if (!musicId || !token) return;
    
    if (!canExportWav) {
      toast.error('WAV export requires Pro+ subscription');
      return;
    }
    
    setProcessing(true);
    setProcessingStatus('Exporting WAV...');
    
    try {
      const response = await axios.post(
        `${API}/music/${musicId}/export-wav`,
        { bit_depth: 16 },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('WAV exported!');
        // Download the file
        const downloadUrl = response.data.r2_url || 
          `${process.env.REACT_APP_BACKEND_URL}${response.data.output_url}`;
        window.open(downloadUrl, '_blank');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'WAV export failed');
    } finally {
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  const handleExportStemsZip = async () => {
    if (!musicId || !token) return;
    
    if (!canExportStems) {
      toast.error('Stems export requires Elite+ subscription');
      return;
    }
    
    setProcessing(true);
    setProcessingStatus('Packaging stems...');
    
    try {
      const response = await axios.post(
        `${API}/music/${musicId}/export-stems-zip`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Stems ZIP ready!');
        const downloadUrl = response.data.r2_url || 
          `${process.env.REACT_APP_BACKEND_URL}${response.data.output_url}`;
        window.open(downloadUrl, '_blank');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Stems export failed');
    } finally {
      setProcessing(false);
      setProcessingStatus('');
    }
  };

  return (
    <div className="collapsible-panel">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="collapsible-trigger"
        disabled={disabled}
        data-testid="video-workflow-toggle"
      >
        <span className="flex items-center gap-2">
          <Video className="w-4 h-4" />
          Video Workflow
          <span className="ml-2 text-xs px-2 py-0.5 bg-green-500/20 text-green-400 rounded-full">
            No Credits
          </span>
        </span>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      
      {isOpen && (
        <div className="collapsible-content animate-fade-in-up space-y-6">
          {/* Processing Status */}
          {processing && (
            <div className="flex items-center gap-2 p-3 bg-primary/10 border border-primary/20 rounded-lg">
              <Loader2 className="w-4 h-4 animate-spin text-primary" />
              <span className="text-sm text-primary">{processingStatus}</span>
            </div>
          )}
          
          {/* Loudness Preset Selector */}
          <div className="space-y-3">
            <label className="text-sm font-medium flex items-center gap-2">
              <Volume2 className="w-4 h-4 text-zinc-400" />
              Optimize For
            </label>
            <Select value={selectedPreset} onValueChange={setSelectedPreset}>
              <SelectTrigger className="w-full" data-testid="loudness-preset-select">
                <SelectValue placeholder="Select platform" />
              </SelectTrigger>
              <SelectContent>
                {loudnessPresets.map((preset) => (
                  <SelectItem key={preset.id} value={preset.id}>
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <Button
              onClick={handleMasterForVideo}
              disabled={processing || disabled}
              className="btn-oracle"
              data-testid="master-for-video-btn"
            >
              <Wand2 className="w-4 h-4 mr-2" />
              Master Output
            </Button>
            
            <Button
              onClick={handleNormalize}
              disabled={processing || disabled}
              variant="outline"
              data-testid="normalize-btn"
            >
              <Volume2 className="w-4 h-4 mr-2" />
              Normalize
            </Button>
          </div>
          
          {/* Fade Controls */}
          <div className="p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl space-y-4">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Music2 className="w-4 h-4 text-zinc-400" />
              Fade Settings
            </h4>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-xs text-zinc-400">Fade In</label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[fadeIn]}
                    onValueChange={(v) => setFadeIn(v[0])}
                    min={0}
                    max={3}
                    step={0.1}
                    className="flex-1"
                  />
                  <span className="text-xs text-zinc-500 w-10">{fadeIn}s</span>
                </div>
              </div>
              
              <div className="space-y-2">
                <label className="text-xs text-zinc-400">Fade Out</label>
                <div className="flex items-center gap-2">
                  <Slider
                    value={[fadeOut]}
                    onValueChange={(v) => setFadeOut(v[0])}
                    min={0}
                    max={5}
                    step={0.1}
                    className="flex-1"
                  />
                  <span className="text-xs text-zinc-500 w-10">{fadeOut}s</span>
                </div>
              </div>
            </div>
            
            <Button
              onClick={handleAutoFade}
              disabled={processing || disabled}
              variant="outline"
              size="sm"
              className="w-full"
              data-testid="apply-fade-btn"
            >
              Apply Fades
            </Button>
          </div>
          
          {/* Sync to Duration */}
          <div className="p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl space-y-4">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <Video className="w-4 h-4 text-zinc-400" />
              Sync to Video Duration
            </h4>
            
            <div className="space-y-2">
              <label className="text-xs text-zinc-400">Target Duration (seconds)</label>
              <div className="flex items-center gap-2">
                <Slider
                  value={[targetDuration]}
                  onValueChange={(v) => setTargetDuration(v[0])}
                  min={5}
                  max={180}
                  step={1}
                  className="flex-1"
                />
                <span className="text-xs text-zinc-500 w-12">{targetDuration}s</span>
              </div>
              <div className="flex gap-2 mt-2">
                {[15, 30, 60, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => setTargetDuration(d)}
                    className={`px-2 py-1 text-xs rounded ${
                      targetDuration === d 
                        ? 'bg-primary text-white' 
                        : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                    }`}
                  >
                    {d}s
                  </button>
                ))}
              </div>
            </div>
            
            <Button
              onClick={handleSyncDuration}
              disabled={processing || disabled}
              variant="outline"
              size="sm"
              className="w-full"
              data-testid="sync-duration-btn"
            >
              Sync Duration
            </Button>
          </div>
          
          {/* Export Options */}
          <div className="pt-4 border-t border-zinc-800 space-y-3">
            <h4 className="text-sm font-medium">Export Options</h4>
            
            <div className="grid grid-cols-2 gap-3">
              {/* WAV Export - Pro+ */}
              <Button
                onClick={handleExportWav}
                disabled={processing || disabled || !canExportWav}
                variant="outline"
                className="relative"
                data-testid="export-wav-btn"
              >
                <FileAudio className="w-4 h-4 mr-2" />
                WAV
                {!canExportWav && (
                  <span className="absolute -top-1 -right-1">
                    <Lock className="w-3 h-3 text-purple-400" />
                  </span>
                )}
              </Button>
              
              {/* Stems ZIP - Elite+ */}
              <Button
                onClick={handleExportStemsZip}
                disabled={processing || disabled || !canExportStems}
                variant="outline"
                className="relative"
                data-testid="export-stems-btn"
              >
                <Archive className="w-4 h-4 mr-2" />
                Stems ZIP
                {!canExportStems && (
                  <span className="absolute -top-1 -right-1">
                    <Lock className="w-3 h-3 text-amber-400" />
                  </span>
                )}
              </Button>
            </div>
            
            <p className="text-xs text-zinc-500">
              {!canExportWav && "WAV export requires Pro+ • "}
              {!canExportStems && "Stems ZIP requires Elite+"}
            </p>
          </div>
          
          {/* Founder badge */}
          {isFounder && (
            <p className="text-xs text-amber-400 flex items-center gap-1">
              <Crown className="w-3 h-3" />
              Founder: Full export access
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoWorkflowPanel;
